import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../firebase';
import { collection, getDocs, orderBy, query, doc, setDoc, serverTimestamp, Timestamp, arrayUnion } from 'firebase/firestore';
import GUEST_LIST from '../data/guestList';
import downloadExport, { EXPORT_TYPES } from '../utils/downloadExport';
import { EMPTY_STATUS, attendsAnyEvent, sanitizeStatus, entryToDocId } from '../utils/rsvpStatus';

const RentalsHub      = lazy(() => import('../components/RentalsHub'));
const VendorContracts = lazy(() => import('../components/VendorContracts'));

const ADMIN_PASSWORD = 'FERRYFOREVER';

// ── Shared style helpers ──────────────────────────────────────────────────────
const thStyle = {
  padding: '10px 16px', textAlign: 'left', fontSize: '0.65rem',
  fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase',
  color: '#6b7280', whiteSpace: 'nowrap',
};
const tdStyle = { padding: '12px 16px', verticalAlign: 'middle' };
const plainBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: 0, border: 0,
  background: 'none', font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit',
  color: 'inherit', cursor: 'pointer',
};
const linkBtnStyle = { ...plainBtnStyle, fontWeight: '600', color: '#374151', textDecoration: 'underline' };

function badge(label, bg, color) {
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px',
      fontSize: '0.68rem', fontWeight: '600', background: bg, color }}>
      {label}
    </span>
  );
}
const MEAL_STYLE = {
  meat:       { bg: '#fef2f2', color: '#991b1b' },
  fish:       { bg: '#eff6ff', color: '#1d4ed8' },
  vegetarian: { bg: '#f0fdf4', color: '#166534' },
};
const MEAL_LABEL = {
  meat:       'Beef Tenderloin',
  fish:       'Chilean Seabass',
  vegetarian: 'Vegetable Lasagna',
};

// Rehearsal Dinner invitees (guestList rehearsal flag: true = whole party,
// array = only those members). Includes unnamed plus-ones ("Guest") so the
// dinner headcount is complete even before that party responds.
const REHEARSAL_INVITEES = GUEST_LIST.flatMap(entry => {
  if (!entry.rehearsal) return [];
  const names = [];
  if (entry.first) names.push(`${entry.first} ${entry.last}`);
  if (entry.partnerFirst && entry.partnerFirst !== 'Guest') {
    names.push(`${entry.partnerFirst} ${entry.partnerLast}`.trim());
  } else if (entry.partnerFirst === 'Guest') {
    names.push('Guest');
  }
  const invited = Array.isArray(entry.rehearsal) ? names.filter(n => entry.rehearsal.includes(n)) : names;
  return invited.map(n => ({ party: entry.formal, name: n }));
});
const REHEARSAL_KEYS = new Set(REHEARSAL_INVITEES.map(p => `${p.party}|${p.name}`));
const isRehearsalInvited = (row) => REHEARSAL_KEYS.has(`${row.party}|${row.name}`);

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ value, label, bg, color, sub }) {
  return (
    <div style={{ background: bg, borderRadius: '12px', padding: '20px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: '2.4rem', fontWeight: '700', color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.06em',
        color, textTransform: 'uppercase', marginTop: '6px' }}>{label}</div>
      {sub != null && (
        <div style={{ fontSize: '0.7rem', color, opacity: 0.7, marginTop: '3px' }}>{sub}</div>
      )}
    </div>
  );
}

// ── Distribution bar ──────────────────────────────────────────────────────────
function DistBar({ label, count, total, bg, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ fontWeight: '600', fontSize: '0.82rem', color: '#374151' }}>{label}</span>
        <span style={{ fontWeight: '700', fontSize: '0.82rem', color }}>{count} <span style={{ fontWeight: '400', color: '#9ca3af' }}>({pct}%)</span></span>
      </div>
      <div style={{ height: '10px', background: '#f3f4f6', borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: bg, borderRadius: '99px', transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

// ── Donut chart (pure CSS conic-gradient) ────────────────────────────────────
function DonutChart({ segments, size = 180, thickness = 28, centerLabel, centerSub }) {
  let cumulative = 0;
  const stops = segments.map(seg => {
    const start = cumulative;
    cumulative += seg.value;
    return `${seg.color} ${start}% ${cumulative}%`;
  }).join(', ');

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: size, height: size, borderRadius: '50%', margin: '0 auto',
        background: cumulative > 0 ? `conic-gradient(${stops})` : '#f3f4f6',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: size - thickness * 2, height: size - thickness * 2,
          borderRadius: '50%', background: 'white',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', lineHeight: 1 }}>{centerLabel}</div>
          {centerSub && <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: '4px' }}>{centerSub}</div>}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '14px', flexWrap: 'wrap' }}>
        {segments.map(seg => (
          <div key={seg.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{seg.label} ({seg.count})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Metric row ───────────────────────────────────────────────────────────────
function MetricRow({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '14px 18px', background: '#f9fafb', borderRadius: '10px', marginBottom: '10px' }}>
      <span style={{ fontSize: '0.82rem', color: '#374151', fontWeight: '500' }}>{label}</span>
      <span style={{ fontSize: '1.3rem', fontWeight: '700', color: color || '#1f2937' }}>{value}</span>
    </div>
  );
}

// ── Split stat (accept vs decline style) ─────────────────────────────────────
function SplitStat({ leftVal, leftLabel, leftColor, leftBg, rightVal, rightLabel, rightColor, rightBg, total }) {
  const leftPct  = total > 0 ? Math.round((leftVal  / total) * 100) : 0;
  const rightPct = total > 0 ? Math.round((rightVal / total) * 100) : 0;
  return (
    <div style={{ background: 'white', borderRadius: '14px', padding: '24px 28px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        <div style={{ background: leftBg, borderRadius: '10px', padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', fontWeight: '700', color: leftColor, lineHeight: 1 }}>{leftVal}</div>
          <div style={{ fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.07em',
            textTransform: 'uppercase', color: leftColor, marginTop: '6px' }}>{leftLabel}</div>
          <div style={{ fontSize: '0.78rem', color: leftColor, opacity: 0.7, marginTop: '2px' }}>{leftPct}%</div>
        </div>
        <div style={{ background: rightBg, borderRadius: '10px', padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', fontWeight: '700', color: rightColor, lineHeight: 1 }}>{rightVal}</div>
          <div style={{ fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.07em',
            textTransform: 'uppercase', color: rightColor, marginTop: '6px' }}>{rightLabel}</div>
          <div style={{ fontSize: '0.78rem', color: rightColor, opacity: 0.7, marginTop: '2px' }}>{rightPct}%</div>
        </div>
      </div>
      {/* progress bar */}
      <div style={{ height: '10px', background: rightBg, borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${leftPct}%`, background: leftColor, opacity: 0.6,
          borderRadius: '99px', transition: 'width 0.4s ease' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px',
        fontSize: '0.68rem', color: '#9ca3af' }}>
        <span>{leftVal} of {total} guests</span>
        <span>{total - leftVal - rightVal > 0 ? `${total - leftVal - rightVal} pending` : ''}</span>
      </div>
    </div>
  );
}

// ── Guest table columns ───────────────────────────────────────────────────────
// Response columns map each row to one option key. That key drives the cell
// badge, the header filter, and the sort order (ascending follows `options`).
const PENDING        = { key: 'pending', label: 'Pending', bg: '#fef3c7', color: '#92400e' };
const NOT_APPLICABLE = { key: 'na',      label: 'N/A' };
const WEDDING_OPTIONS = [
  { key: 'accepts',  label: 'Accepts',  bg: '#d1fae5', color: '#065f46' },
  { key: 'declines', label: 'Declines', bg: '#f3f4f6', color: '#6b7280' },
  PENDING,
];
const EVENT_OPTIONS = [
  { key: 'yes', label: 'Attending',     bg: '#d1fae5', color: '#065f46' },
  { key: 'no',  label: 'Not Attending', bg: '#f3f4f6', color: '#6b7280' },
  PENDING,
];
const MEAL_OPTIONS = [
  ...Object.keys(MEAL_LABEL).map(key => ({ key, label: MEAL_LABEL[key], ...MEAL_STYLE[key] })),
  PENDING, NOT_APPLICABLE,
];
const DIETARY_OPTIONS = [
  { key: 'yes', label: 'Has Restrictions' },
  { key: 'no',  label: 'None' },
  PENDING, NOT_APPLICABLE,
];

const yesNo = v => (v === 'yes' || v === 'no' ? v : 'pending');

function optionCell(options, key) {
  if (key === 'na') return <span style={{ color: '#d1d5db' }}>N/A</span>;
  const opt = options.find(o => o.key === key);
  return badge(opt?.label || key, opt?.bg || '#f3f4f6', opt?.color || '#6b7280');
}

const GUEST_COLUMNS = {
  'Name': {
    sortValue: r => r.name,
    render:    r => <strong>{r.name}</strong>,
  },
  'Party': {
    sortValue: r => r.party,
    style:     { color: '#6b7280', fontSize: '0.75rem' },
    render:    r => r.party,
  },
  'Wedding': {
    options: WEDDING_OPTIONS,
    value:   r => (r.status?.attending === 'accepts' || r.status?.attending === 'declines' ? r.status.attending : 'pending'),
  },
  'Rehearsal': {
    options: [...EVENT_OPTIONS, NOT_APPLICABLE],
    value:   r => (isRehearsalInvited(r) ? yesNo(r.status?.rehearsal) : 'na'),
  },
  'Welcome Party': {
    options: EVENT_OPTIONS,
    value:   r => yesNo(r.status?.welcomeParty),
  },
  'Meal': {
    options: MEAL_OPTIONS,
    value:   r => {
      const s = r.status || {};
      if (s.attending === 'declines') return 'na';
      return (s.attending === 'accepts' && s.meal) || 'pending';
    },
  },
  'Dietary': {
    options: DIETARY_OPTIONS,
    // Only asked of guests attending something — until the wedding is answered it's still pending
    value:   r => {
      const s = r.status || {};
      if (s.dietary === 'yes' || s.dietary === 'no') return s.dietary;
      return !s.attending || attendsAnyEvent(s) ? 'pending' : 'na';
    },
    style:   { maxWidth: '220px' },
    render:  (r, key) => key === 'yes'
      ? <span style={{ color: '#c2410c', fontSize: '0.78rem' }}>{r.status.dietaryNote || 'Yes (no details)'}</span>
      : key === 'no'
      ? <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>None</span>
      : optionCell(DIETARY_OPTIONS, key),
  },
  'Updated': {
    sortValue: r => r.updatedAt?.toMillis?.() ?? null,
    style:     { color: '#9ca3af', whiteSpace: 'nowrap', fontSize: '0.75rem' },
    render:    r => {
      const ts = r.updatedAt?.toDate?.();
      return ts ? ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
    },
  },
};

// null (no timestamp yet, or the question doesn't apply) sorts last in either direction
function sortValue(col, row) {
  const def = GUEST_COLUMNS[col];
  if (def.sortValue) return def.sortValue(row);
  const key = def.value(row);
  if (key === 'na') return null;
  const i = def.options.findIndex(o => o.key === key);
  return i === -1 ? def.options.length : i;
}

// filters: { [col]: [option key, …] } — a column with nothing selected matches every row
const matchesFilters = (row, filters, skipCol) =>
  Object.entries(filters).every(([col, keys]) =>
    col === skipCol || !keys.length || keys.includes(GUEST_COLUMNS[col].value(row)));

function SortIcon({ dir }) {
  return (
    <svg width="7" height="11" viewBox="0 0 7 11" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M3.5 0 7 4.5H0z"  fill={dir === 'asc'  ? '#1f2937' : '#d1d5db'} />
      <path d="M3.5 11 0 6.5h7z" fill={dir === 'desc' ? '#1f2937' : '#d1d5db'} />
    </svg>
  );
}

function FilterIcon({ active }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true" fill={active ? 'currentColor' : 'none'}
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

// ── Inline editing ────────────────────────────────────────────────────────────
// Which status field each response column edits. `applies` mirrors the guest
// form: no meal unless attending the wedding, no dietary question unless
// attending something, no rehearsal answer unless invited.
const EDIT_FIELDS = {
  'Wedding':       { field: 'attending',    options: WEDDING_OPTIONS },
  'Rehearsal':     { field: 'rehearsal',    options: EVENT_OPTIONS, applies: r => isRehearsalInvited(r) },
  'Welcome Party': { field: 'welcomeParty', options: EVENT_OPTIONS },
  'Meal':          { field: 'meal',         options: MEAL_OPTIONS.filter(o => o.key !== 'na'),    applies: (r, d) => d.attending === 'accepts' },
  'Dietary':       { field: 'dietary',      options: DIETARY_OPTIONS.filter(o => o.key !== 'na'), applies: (r, d) => attendsAnyEvent(d) },
};
const rowKey = r => `${r.party}|${r.name}`;
const inputStyle = {
  font: 'inherit', fontSize: '0.75rem', padding: '4px 8px', borderRadius: '8px',
  border: '1.5px solid #d1d5db', background: 'white', color: '#1f2937', width: '100%', minWidth: '96px',
};
const smallBtnStyle = {
  padding: '5px 12px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '600',
  border: '1.5px solid', cursor: 'pointer',
};

// Human-readable list of what changed between two saved statuses
const HISTORY_FIELDS = [
  ['attending', 'Wedding', WEDDING_OPTIONS], ['rehearsal', 'Rehearsal', EVENT_OPTIONS],
  ['welcomeParty', 'Welcome Party', EVENT_OPTIONS], ['meal', 'Meal', MEAL_OPTIONS],
  ['dietary', 'Dietary', DIETARY_OPTIONS], ['dietaryNote', 'Dietary note', null],
];
function diffStatus(before, after) {
  const b = before || {}, a = after || {};
  return HISTORY_FIELDS.flatMap(([field, label, opts]) => {
    const bv = b[field] || '', av = a[field] || '';
    if (bv === av) return [];
    const show = v => opts ? (opts.find(o => o.key === v)?.label || 'Pending') : (v || '—');
    return [`${label}: ${show(bv)} → ${show(av)}`];
  });
}

// ── Header filter popover ─────────────────────────────────────────────────────
// Portaled to <body> with fixed positioning so the table's overflow wrappers
// don't clip it; re-measures its header cell after renders, scrolls and resizes.
const MENU_WIDTH = 220;

function FilterMenu({ anchor, label, options, counts, selected, onToggle, onClear, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const r    = (anchor.closest('th') || anchor).getBoundingClientRect();
    const top  = r.bottom + 4;
    const left = Math.max(8, Math.min(r.left + 8, window.innerWidth - MENU_WIDTH - 8));
    setPos(p => (p && p.top === top && p.left === left ? p : { top, left }));
  }, [anchor]);

  // Every render: applying a filter adds the summary bar, which pushes the header down
  useLayoutEffect(() => { place(); });

  useEffect(() => {
    const onPointer = e => {
      if (!ref.current?.contains(e.target) && !anchor.contains(e.target)) onClose();
    };
    const onKey = e => {
      if (e.key === 'Escape') { onClose(); anchor.focus(); }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchor, onClose, place]);

  if (!pos) return null;
  return createPortal(
    <div ref={ref} role="dialog" aria-label={`Filter ${label}`} style={{
      position: 'fixed', top: pos.top, left: pos.left, width: MENU_WIDTH, zIndex: 1100,
      background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.12)', padding: '6px',
      fontSize: '0.8rem', lineHeight: 1.4, color: '#374151',
    }}>
      {options.map((o, i) => (
        <label key={o.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 10px',
          borderRadius: '6px', cursor: 'pointer', color: counts[o.key] ? '#374151' : '#9ca3af' }}>
          <input type="checkbox" checked={selected.includes(o.key)} onChange={() => onToggle(o.key)}
            autoFocus={i === 0} style={{ margin: 0, accentColor: '#1f2937', cursor: 'pointer' }} />
          <span style={{ flex: 1 }}>{o.label}</span>
          <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{counts[o.key] || 0}</span>
        </label>
      ))}
      <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '4px', padding: '8px 10px 4px' }}>
        <button type="button" onClick={onClear} disabled={!selected.length}
          style={{ ...linkBtnStyle, opacity: selected.length ? 1 : 0.4, cursor: selected.length ? 'pointer' : 'default' }}>
          Clear filter
        </button>
      </div>
    </div>,
    document.body
  );
}

// ── Data table ────────────────────────────────────────────────────────────────
// Header labels sort (ascending → descending → original order); response
// columns also get a funnel that filters by answer.
function GuestTable({ rows, columns, onSave }) {
  const [sort,    setSort]    = useState(null);  // { col, dir: 'asc' | 'desc' }
  const [filters, setFilters] = useState({});    // { [col]: [option key, …] }
  const [menu,    setMenu]    = useState(null);  // { col, anchor } — open filter popover
  const [editing, setEditing] = useState(null);  // { key, draft, saving, error } — row being edited
  const closeMenu = useCallback(() => setMenu(null), []);
  if (!rows) return null;

  const startEdit = row => setEditing({ key: rowKey(row), draft: { ...EMPTY_STATUS, ...(row.status || {}) }, saving: false, error: false });
  const patchDraft = patch => setEditing(e => ({ ...e, draft: { ...e.draft, ...patch }, error: false }));
  const saveEdit = async row => {
    const status = sanitizeStatus(editing.draft, isRehearsalInvited(row));
    if (!diffStatus(row.status, status).length) { setEditing(null); return; }
    setEditing(e => ({ ...e, saving: true, error: false }));
    try {
      await onSave(row, status);
      setEditing(null);
    } catch (err) {
      console.error(err);
      setEditing(e => ({ ...e, saving: false, error: true }));
    }
  };
  const editCell = (row, col) => {
    const ed = EDIT_FIELDS[col];
    if (ed.applies && !ed.applies(row, editing.draft)) return <span style={{ color: '#d1d5db' }}>N/A</span>;
    const value = editing.draft[ed.field] ?? 'pending';
    return (
      <div style={{ display: 'grid', gap: '4px' }}>
        <select value={value} aria-label={`${col} for ${row.name}`} disabled={editing.saving}
          onChange={e => patchDraft({ [ed.field]: e.target.value === 'pending' ? null : e.target.value })} style={inputStyle}>
          {ed.options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        {col === 'Dietary' && value === 'yes' && (
          <input value={editing.draft.dietaryNote || ''} placeholder="Allergies or needs…" aria-label={`Dietary note for ${row.name}`}
            disabled={editing.saving} onChange={e => patchDraft({ dietaryNote: e.target.value })} style={inputStyle} />
        )}
      </div>
    );
  };
  const colCount = columns.length + (onSave ? 1 : 0);

  const toggleSort = col => setSort(s =>
    s?.col !== col ? { col, dir: 'asc' } : s.dir === 'asc' ? { col, dir: 'desc' } : null);
  const toggleFilter = (col, key) => setFilters(f => {
    const keys = f[col] || [];
    return { ...f, [col]: keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key] };
  });
  const clearFilter = col => setFilters(f => ({ ...f, [col]: [] }));

  const active  = Object.entries(filters).filter(([, keys]) => keys.length > 0);
  const matches = (row, skipCol) => matchesFilters(row, filters, skipCol);

  const visible = rows.filter(r => matches(r));
  if (sort) {
    const dir = sort.dir === 'asc' ? 1 : -1;
    visible.sort((a, b) => {
      const va = sortValue(sort.col, a);
      const vb = sortValue(sort.col, b);
      if (va == null) return vb == null ? 0 : 1;
      if (vb == null) return -1;
      return (typeof va === 'string' ? va.localeCompare(vb) : va - vb) * dir;
    });
  }

  // Open popover: options that occur in this table, counted under the other columns' filters
  const menuCounts = {};
  let menuOptions = [];
  if (menu) {
    const def = GUEST_COLUMNS[menu.col];
    const present = new Set(rows.map(r => def.value(r)));
    rows.forEach(r => {
      if (!matches(r, menu.col)) return;
      const key = def.value(r);
      menuCounts[key] = (menuCounts[key] || 0) + 1;
    });
    menuOptions = def.options.filter(o => present.has(o.key) || filters[menu.col]?.includes(o.key));
  }

  return (
    <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
      {active.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px',
          padding: '10px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.75rem', color: '#6b7280' }}>
          <span>Showing <strong style={{ color: '#1f2937' }}>{visible.length}</strong> of {rows.length} guests</span>
          {active.map(([col, keys]) => (
            <span key={col} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '3px 4px 3px 10px', borderRadius: '12px', background: '#f3f4f6',
              color: '#374151', fontSize: '0.7rem', fontWeight: '600' }}>
              {col}: {GUEST_COLUMNS[col].options.filter(o => keys.includes(o.key)).map(o => o.label).join(', ')}
              <button type="button" onClick={() => clearFilter(col)} aria-label={`Clear ${col} filter`}
                style={{ ...plainBtnStyle, padding: '0 6px', fontSize: '0.9rem', lineHeight: 1, color: '#6b7280' }}>
                ×
              </button>
            </span>
          ))}
          <button type="button" onClick={() => setFilters({})} style={{ ...linkBtnStyle, marginLeft: 'auto' }}>
            Clear all
          </button>
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {columns.map(c => {
                const dir      = sort?.col === c ? sort.dir : null;
                const filtered = (filters[c] || []).length > 0;
                return (
                  <th key={c} style={thStyle} aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <button type="button" onClick={() => toggleSort(c)} title={`Sort by ${c}`}
                        style={{ ...plainBtnStyle, color: dir ? '#1f2937' : 'inherit' }}>
                        {c}
                        <SortIcon dir={dir} />
                      </button>
                      {GUEST_COLUMNS[c].options && (
                        <button type="button" title={`Filter ${c}`} aria-label={`Filter ${c}`}
                          aria-haspopup="dialog" aria-expanded={menu?.col === c}
                          onClick={e => {
                            const anchor = e.currentTarget;
                            setMenu(m => (m?.col === c ? null : { col: c, anchor }));
                          }}
                          style={{ ...plainBtnStyle, padding: '4px', margin: '-4px 0', borderRadius: '4px',
                            color: filtered ? '#1f2937' : '#9ca3af',
                            background: filtered || menu?.col === c ? '#e5e7eb' : 'none' }}>
                          <FilterIcon active={filtered} />
                        </button>
                      )}
                    </span>
                  </th>
                );
              })}
              {onSave && <th style={thStyle} aria-label="Edit" />}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={colCount} style={{ textAlign: 'center', padding: '32px', color: '#9ca3af' }}>
                {rows.length === 0 ? 'No responses yet' : 'No guests match these filters'}
              </td></tr>
            )}
            {visible.map(row => {
              const isEditing = editing?.key === rowKey(row);
              return (
                <tr key={rowKey(row)} style={{ borderTop: '1px solid #f3f4f6', background: isEditing ? '#fffbeb' : 'transparent' }}>
                  {columns.map(c => {
                    const def = GUEST_COLUMNS[c];
                    if (isEditing && EDIT_FIELDS[c]) return <td key={c} style={tdStyle}>{editCell(row, c)}</td>;
                    const key = def.value?.(row);
                    return (
                      <td key={c} style={{ ...tdStyle, ...def.style }}>
                        {def.render ? def.render(row, key) : optionCell(def.options, key)}
                      </td>
                    );
                  })}
                  {onSave && (
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {isEditing ? (
                        <div style={{ display: 'inline-grid', gap: '6px', justifyItems: 'end' }}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button type="button" onClick={() => setEditing(null)} disabled={editing.saving}
                              style={{ ...smallBtnStyle, borderColor: '#d1d5db', background: 'white', color: '#374151' }}>
                              Cancel
                            </button>
                            <button type="button" onClick={() => saveEdit(row)} disabled={editing.saving}
                              style={{ ...smallBtnStyle, borderColor: '#1f2937', background: '#1f2937', color: 'white',
                                opacity: editing.saving ? 0.6 : 1 }}>
                              {editing.saving ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                          {editing.error && <span style={{ fontSize: '0.7rem', color: '#ef4444' }}>Couldn't save — try again</span>}
                        </div>
                      ) : (
                        <button type="button" onClick={() => startEdit(row)} title={`Edit ${row.name}`} aria-label={`Edit ${row.name}`}
                          style={{ ...plainBtnStyle, padding: '4px', borderRadius: '4px', color: '#9ca3af' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                          </svg>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {menu && (
        <FilterMenu
          anchor={menu.anchor}
          label={menu.col}
          options={menuOptions}
          counts={menuCounts}
          selected={filters[menu.col] || []}
          onToggle={key => toggleFilter(menu.col, key)}
          onClear={() => clearFilter(menu.col)}
          onClose={closeMenu}
        />
      )}
    </div>
  );
}

// ── Modal shell ───────────────────────────────────────────────────────────────
// Centered dialog portaled to <body>; Escape or a click on the backdrop closes it.
function Modal({ title, onClose, width = 560, children }) {
  const panel = useRef(null);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div onMouseDown={e => { if (!panel.current?.contains(e.target)) onClose(); }} style={{
      position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(17,24,39,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div ref={panel} role="dialog" aria-modal="true" aria-label={title} style={{
        background: 'white', borderRadius: '14px', width: '100%', maxWidth: width, maxHeight: '90vh',
        overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', padding: '24px 28px',
        fontSize: '0.82rem', color: '#374151',
      }}>
        <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: '1.05rem', fontWeight: '700',
          color: '#1f2937', margin: '0 0 4px' }}>{title}</h2>
        {children}
      </div>
    </div>,
    document.body
  );
}

// ── Version history ───────────────────────────────────────────────────────────
// Every admin save is logged on the party's RSVP doc; this lists them newest
// first and lets you put a guest back to how they were before that change.
function HistoryModal({ entries, onRestore, onClose }) {
  const [busy,  setBusy]  = useState(null);   // index being restored
  const [error, setError] = useState(null);
  const restore = async (entry, i) => {
    setBusy(i);
    setError(null);
    try { await onRestore(entry); }
    catch (err) { console.error(err); setError(i); }
    finally { setBusy(null); }
  };
  return (
    <Modal title="Version history" onClose={onClose} width={620}>
      <p style={{ margin: '0 0 16px', fontSize: '0.75rem', color: '#6b7280' }}>
        Changes made here in the admin console, newest first. Restoring puts that guest back to how they were
        before the change — and is logged as a new version, so nothing is ever lost.
      </p>
      {entries.length === 0 && (
        <p style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af' }}>No changes yet</p>
      )}
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '8px' }}>
        {entries.map((h, i) => {
          const when    = h.changedAt?.toDate?.();
          const changes = diffStatus(h.before, h.after);
          return (
            <li key={`${h.party}|${h.name}|${when?.getTime()}`} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start',
              padding: '12px 14px', borderRadius: '10px', border: '1px solid #f3f4f6', background: '#f9fafb' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: '700', color: '#9ca3af' }}>v{entries.length - i}</span>
                  <strong style={{ color: '#1f2937' }}>{h.name}</strong>
                  <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>{h.party}</span>
                  {h.source === 'restore' && badge('Restored', '#dbeafe', '#1d4ed8')}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#374151', display: 'grid', gap: '2px' }}>
                  {changes.length ? changes.map(c => <span key={c}>{c}</span>) : <span style={{ color: '#9ca3af' }}>No visible change</span>}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '4px' }}>
                  {when ? when.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                  {error === i && <span style={{ color: '#ef4444', marginLeft: '8px' }}>Couldn't restore — try again</span>}
                </div>
              </div>
              <button type="button" onClick={() => restore(h, i)} disabled={busy != null}
                style={{ ...smallBtnStyle, borderColor: '#d1d5db', background: 'white', color: '#374151',
                  whiteSpace: 'nowrap', opacity: busy != null && busy !== i ? 0.5 : 1 }}>
                {busy === i ? 'Restoring…' : 'Restore previous'}
              </button>
            </li>
          );
        })}
      </ol>
    </Modal>
  );
}

// ── Export ────────────────────────────────────────────────────────────────────
// Title / first / last for every invitee, keyed like the table rows, plus the
// party's position in GUEST_LIST so the Aisle Planner file keeps parties
// together with the primary guest first.
const GUEST_INFO = new Map();
GUEST_LIST.forEach((entry, order) => {
  GUEST_INFO.set(`${entry.formal}|${entry.first} ${entry.last}`,
    { title: entry.title, first: entry.first, last: entry.last, order, primary: true });
  if (entry.partnerFirst === 'Guest') {
    GUEST_INFO.set(`${entry.formal}|Guest`, { title: '', first: 'Guest', last: entry.last, order, primary: false });
  } else if (entry.partnerFirst) {
    GUEST_INFO.set(`${entry.formal}|${entry.partnerFirst} ${entry.partnerLast}`.trim(),
      { title: entry.partnerTitle, first: entry.partnerFirst, last: entry.partnerLast, order, primary: false });
  }
});
function guestInfo(row) {
  const info = GUEST_INFO.get(`${row.party}|${row.name}`);
  if (info) return info;
  // Response doc whose name no longer matches the guest list — best-effort split
  const parts = row.name.trim().split(' ');
  return { title: '', first: parts.slice(0, -1).join(' ') || parts[0], last: parts.length > 1 ? parts.at(-1) : '',
    order: Infinity, primary: true };
}

// Katie's "AP-Guests-Template-by-Individual": Sheet1, Calibri 12 with a bold
// header, these 14 columns in this order, primary guest first within a party
// and a blank row between parties. Column widths copied from the template.
const AP_HEADERS = ['Last Name', 'First Name', 'Title', 'Suffix', 'Street Address', 'Address Line 2',
  'City', 'State', 'Zip', 'Country', 'Phone', 'Email', 'Group', 'Guest List'];
const AP_WIDTHS  = { E: 35.7, G: 15.3, H: 13.3, J: 11.8, K: 12.3, L: 27.3, M: 19.3 };

const EXPORT_FORMATS = [
  { key: 'aisle', label: 'Aisle Planner import',
    desc: "Katie's template, ready to upload: names and titles only (the site doesn't store addresses, phone or email), primary guest first, blank row between parties." },
  { key: 'full', label: 'Full detail',
    desc: 'Everything in the table below — one row per guest with wedding, rehearsal, welcome party, meal and dietary answers.' },
];
const EXPORT_FILTER_COLS = ['Wedding', 'Rehearsal', 'Welcome Party', 'Meal', 'Dietary'];
// Optional "Full detail" columns from guestAddresses.js — party-level, so every member gets them
const PERSONAL_OPTIONS = [
  { key: 'address', label: 'Address', width: 44 },
  { key: 'email',   label: 'Email',   width: 30 },
  { key: 'phone',   label: 'Phone',   width: 16 },
];
const formatAddress = a => [
  a.street, a.line2,
  [[a.city, a.state].filter(Boolean).join(', '), a.zip].filter(Boolean).join(' '),
  a.country,
].filter(Boolean).join(', ');
const EXPORT_DEFAULT_FILTERS = { Wedding: ['accepts'] };

const optionLabel = (col, key) => GUEST_COLUMNS[col].options.find(o => o.key === key)?.label || (key === 'na' ? 'N/A' : key);

async function buildExport(format, rows, personal = []) {
  const stamp = new Date().toLocaleDateString('en-CA'); // local YYYY-MM-DD
  if (format === 'aisle') {
    // Addresses ship in their own chunk — only fetched for this export
    const { default: ADDRESSES } = await import('../data/guestAddresses');
    const sorted = rows.map(r => ({ r, info: guestInfo(r) }))
      .sort((a, b) => a.info.order - b.info.order || (a.info.primary ? -1 : 1) || a.r.name.localeCompare(b.r.name));
    const out = [];
    sorted.forEach(({ r, info }, i) => {
      const firstOfParty = i === 0 || sorted[i - 1].r.party !== r.party;
      if (i > 0 && firstOfParty) out.push(null);
      // Template rule: address, phone and email go on the party's first row only
      const a = firstOfParty ? ADDRESSES[r.party] || {} : {};
      out.push([info.last, info.first, info.title, '', a.street || '', a.line2 || '', a.city || '', a.state || '',
        a.zip ?? '', a.country || '', a.phone || '', a.email || '', '', '']);
    });
    return { filename: `aisle-planner-guests-${stamp}`, headers: AP_HEADERS, rows: out, widths: AP_WIDTHS };
  }
  const extras    = PERSONAL_OPTIONS.filter(o => personal.includes(o.key));
  const ADDRESSES = extras.length ? (await import('../data/guestAddresses')).default : {};
  const cols   = ['Name', 'Party', ...EXPORT_FILTER_COLS, ...extras.map(o => o.label), 'Updated'];
  const widths = [24, 40, 12, 16, 16, 20, 28, ...extras.map(o => o.width), 22];
  const out = rows.map(r => cols.map(col => {
    const extra = extras.find(o => o.label === col);
    if (extra) {
      const a = ADDRESSES[r.party] || {};
      return extra.key === 'address' ? formatAddress(a) : a[extra.key] || '';
    }
    const def = GUEST_COLUMNS[col];
    if (!def.options) return col === 'Updated'
      ? (r.updatedAt?.toDate?.()?.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) || '')
      : r[col.toLowerCase()];
    const key = def.value(r);
    if (col === 'Dietary' && key === 'yes') return r.status.dietaryNote || 'Yes (no details)';
    return optionLabel(col, key);
  }));
  return { filename: `rsvp-responses-${stamp}`, headers: cols, rows: out,
    widths: Object.fromEntries(widths.map((w, i) => [String.fromCharCode(65 + i), w])) };
}

// Toggle pill used by the export dialog's option groups
function Pill({ on, onClick, children }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} style={{
      padding: '5px 12px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '600',
      border: '1.5px solid', cursor: 'pointer',
      background: on ? '#1f2937' : 'white', borderColor: on ? '#1f2937' : '#d1d5db',
      color: on ? 'white' : '#374151',
    }}>
      {children}
    </button>
  );
}

function ExportModal({ rows, onClose }) {
  const [format,    setFormat]    = useState('aisle');
  const [type,      setType]      = useState('xlsx');
  const [filters,   setFilters]   = useState(EXPORT_DEFAULT_FILTERS);
  const [personal,  setPersonal]  = useState([]);
  const [exporting, setExporting] = useState(false);
  const [error,     setError]     = useState(false);

  const toggle = (col, key) => setFilters(f => {
    const keys = f[col] || [];
    return { ...f, [col]: keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key] };
  });
  const selected = rows.filter(r => matchesFilters(r, filters));

  const handleExport = async () => {
    setExporting(true);
    setError(false);
    try {
      const active = EXPORT_FILTER_COLS.filter(c => filters[c]?.length)
        .map(c => `${c}: ${filters[c].map(k => optionLabel(c, k)).join(', ')}`);
      await downloadExport(type, {
        ...(await buildExport(format, selected, personal)),
        title:    format === 'full' ? 'RSVP Responses' : 'Guest List',
        subtitle: [`${selected.length} guests`, ...(active.length ? active : ['All guests']),
          `Exported ${new Date().toLocaleDateString('en-US', { dateStyle: 'medium' })}`].join('  ·  '),
      });
      onClose();
    } catch (err) {
      setError(true);
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal title="Export guests" onClose={onClose}>
        <p style={{ margin: '0 0 20px', fontSize: '0.75rem', color: '#6b7280' }}>
          Choose a format, then narrow down who's included. Leave a group untouched to include everyone.
        </p>

        {/* Format */}
        <div role="radiogroup" aria-label="Format" style={{ display: 'grid', gap: '8px', marginBottom: '20px' }}>
          {EXPORT_FORMATS.map(f => {
            const on = format === f.key;
            return (
              <label key={f.key} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '12px 14px',
                borderRadius: '10px', border: `1.5px solid ${on ? '#1f2937' : '#e5e7eb'}`,
                background: on ? '#f9fafb' : 'white', cursor: 'pointer' }}>
                <input type="radio" name="export-format" checked={on} onChange={() => setFormat(f.key)}
                  autoFocus={f.key === 'aisle'} style={{ marginTop: '3px', accentColor: '#1f2937' }} />
                <span>
                  <span style={{ display: 'block', fontWeight: '600', color: '#1f2937' }}>{f.label}</span>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: '#6b7280', marginTop: '2px' }}>{f.desc}</span>
                </span>
              </label>
            );
          })}
        </div>

        {/* File type */}
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 14px' }}>
          <legend style={{ ...thStyle, padding: '0 0 6px' }}>File type</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {EXPORT_TYPES.map(t => (
              <Pill key={t.key} on={type === t.key} onClick={() => setType(t.key)}>{t.label}</Pill>
            ))}
          </div>
          {format === 'aisle' && type !== 'xlsx' && (
            <p style={{ margin: '8px 0 0', fontSize: '0.72rem', color: '#92400e' }}>
              Aisle Planner only imports Excel files — use CSV or PDF for your own reference.
            </p>
          )}
        </fieldset>

        {/* Filters */}
        {EXPORT_FILTER_COLS.map(col => {
          const keys    = filters[col] || [];
          const present = new Set(rows.map(r => GUEST_COLUMNS[col].value(r)));
          const counts  = {};
          rows.forEach(r => {
            if (!matchesFilters(r, filters, col)) return;
            const k = GUEST_COLUMNS[col].value(r);
            counts[k] = (counts[k] || 0) + 1;
          });
          return (
            <fieldset key={col} style={{ border: 0, padding: 0, margin: '0 0 14px' }}>
              <legend style={{ ...thStyle, padding: '0 0 6px' }}>{col}</legend>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {GUEST_COLUMNS[col].options.filter(o => present.has(o.key) || keys.includes(o.key)).map(o => (
                  <Pill key={o.key} on={keys.includes(o.key)} onClick={() => toggle(col, o.key)}>
                    {o.label} <span style={{ opacity: 0.6, fontWeight: '500' }}>{counts[o.key] || 0}</span>
                  </Pill>
                ))}
              </div>
            </fieldset>
          );
        })}

        {/* Personal information — extra columns, Full detail only */}
        {format === 'full' && (
          <fieldset style={{ border: 0, padding: 0, margin: '0 0 14px' }}>
            <legend style={{ ...thStyle, padding: '0 0 6px' }}>Personal information</legend>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {PERSONAL_OPTIONS.map(o => (
                <Pill key={o.key} on={personal.includes(o.key)}
                  onClick={() => setPersonal(p => p.includes(o.key) ? p.filter(k => k !== o.key) : [...p, o.key])}>
                  {o.label}
                </Pill>
              ))}
            </div>
            <p style={{ margin: '8px 0 0', fontSize: '0.72rem', color: '#9ca3af' }}>
              Adds columns from the address list. Details are per party, so everyone in a party gets the same ones.
            </p>
          </fieldset>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '20px', paddingTop: '16px',
          borderTop: '1px solid #f3f4f6' }}>
          <span style={{ fontSize: '0.75rem', color: error ? '#ef4444' : '#6b7280' }}>
            {error ? "Couldn't build the file — try again" : <><strong style={{ color: '#1f2937' }}>{selected.length}</strong> of {rows.length} guests</>}
          </span>
          <button type="button" onClick={() => setFilters(EXPORT_DEFAULT_FILTERS)} style={linkBtnStyle}>Reset</button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 18px', borderRadius: '20px', fontSize: '0.75rem',
              fontWeight: '600', border: '1.5px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="button" onClick={handleExport} disabled={exporting || selected.length === 0} style={{
              padding: '8px 18px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600',
              border: '1.5px solid #1f2937', background: '#1f2937', color: 'white',
              cursor: exporting || selected.length === 0 ? 'default' : 'pointer',
              opacity: exporting || selected.length === 0 ? 0.5 : 1 }}>
              {exporting ? 'Building…' : `Download .${type}`}
            </button>
          </div>
        </div>
    </Modal>
  );
}

// RSVP party docs, most recently updated first
async function fetchRsvpRecords() {
  const q    = query(collection(db, 'rsvps'), orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);
  const rows = [];
  snap.forEach(d => rows.push({ id: d.id, ...d.data() }));
  return rows;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Admin() {
  const [authed,  setAuthed]  = useState(false);
  const [pw,      setPw]      = useState('');
  const [pwError, setPwError] = useState(false);
  const [showPw,  setShowPw]  = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [tab,     setTab]     = useState('wedding');
  const [loadedAt,     setLoadedAt]     = useState(null);
  const [refreshing,   setRefreshing]   = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [exportOpen,   setExportOpen]   = useState(false);
  const [historyOpen,  setHistoryOpen]  = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    if (pw.toUpperCase().trim() === ADMIN_PASSWORD) setAuthed(true);
    else setPwError(true);
  };

  useEffect(() => {
    if (!authed) return;
    setLoading(true);
    (async () => {
      try {
        setRecords(await fetchRsvpRecords());
        setLoadedAt(new Date());
      } catch (err) {
        setError('Could not load responses. Check your Firebase config.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [authed]);

  // Re-fetch in place: leaves `loading` alone so the dashboard (and the table's
  // sort/filters) stays mounted, and a failed refresh keeps the current data.
  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshError(false);
    try {
      setRecords(await fetchRsvpRecords());
      setLoadedAt(new Date());
    } catch (err) {
      setRefreshError(true);
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  // Flatten rows — normalise old string-style status if any
  const allRows = records.flatMap(rec =>
    Object.entries(rec.responses || {}).map(([name, status]) => ({
      party:     rec.party,
      name,
      status:    typeof status === 'object' ? status : { attending: status === 'attending' ? 'accepts' : 'declines' },
      updatedAt: rec.updatedAt,
    }))
  );

  const accepting     = allRows.filter(r => r.status?.attending === 'accepts');
  const declining     = allRows.filter(r => r.status?.attending === 'declines');
  const noResponse    = allRows.filter(r => r.status?.attending !== 'accepts' && r.status?.attending !== 'declines');
  const welcomeYes    = allRows.filter(r => r.status?.welcomeParty === 'yes');
  const welcomeNo     = allRows.filter(r => r.status?.welcomeParty === 'no');
  const rehearsalRows = allRows.filter(isRehearsalInvited);
  const rehearsalYes  = rehearsalRows.filter(r => r.status?.rehearsal === 'yes');
  const rehearsalNo   = rehearsalRows.filter(r => r.status?.rehearsal === 'no');
  // Invitees with no response doc at all (covers plus-one "Guest" members too)
  const rehearsalPending = REHEARSAL_INVITEES
    .filter(p => !rehearsalRows.some(r => r.party === p.party && r.name === p.name))
    .map(p => ({ name: p.name, party: p.party, status: {}, updatedAt: null }));
  const meatRows      = accepting.filter(r => r.status?.meal === 'meat');
  const fishRows      = accepting.filter(r => r.status?.meal === 'fish');
  const vegRows       = accepting.filter(r => r.status?.meal === 'vegetarian');
  const dietaryRows   = allRows.filter(r => r.status?.dietary === 'yes');
  const totalMeals    = meatRows.length + fishRows.length + vegRows.length;
  // Build a set of party|name keys that have already responded — party-scoped so
  // duplicate names (two "James Donahue"s, the unnamed plus-one "Guest"s) don't collide
  const respondedKeys = new Set(allRows.map(r => `${r.party}|${r.name}`));

  // Build full guest list rows: expand each GUEST_LIST entry into individual guests
  const pendingGuests = GUEST_LIST.flatMap(entry => {
    const guests = [];
    const firstName = `${entry.first} ${entry.last}`;
    if (entry.first && entry.last && !respondedKeys.has(`${entry.formal}|${firstName}`)) {
      guests.push({ name: firstName, party: entry.formal, status: {}, updatedAt: null });
    }
    const partnerName = entry.partnerFirst === 'Guest' ? 'Guest' : `${entry.partnerFirst} ${entry.partnerLast}`.trim();
    if (entry.partnerFirst && partnerName && !respondedKeys.has(`${entry.formal}|${partnerName}`)) {
      guests.push({ name: partnerName, party: entry.formal, status: {}, updatedAt: null });
    }
    return guests;
  });

  // Admin edits logged on each party doc, newest first
  const history = records
    .flatMap(rec => (rec.history || []).map(h => ({ ...h, party: rec.party })))
    .sort((a, b) => (b.changedAt?.toMillis?.() || 0) - (a.changedAt?.toMillis?.() || 0));

  // Write one guest's answers (inline edit or history restore) and log the
  // change on the party doc, then re-read so every tab and the history agree.
  const saveResponse = async (party, name, status, source = 'edit') => {
    const rec     = records.find(r => r.party === party);
    const current = allRows.find(r => r.party === party && r.name === name)?.status;
    const before  = current && Object.keys(current).length ? current : null;
    await setDoc(doc(db, 'rsvps', rec?.id || entryToDocId(party)), {
      party,
      responses: { [name]: status },
      updatedAt: serverTimestamp(),
      history:   arrayUnion({ name, before, after: status, changedAt: Timestamp.now(), source }),
    }, { merge: true });
    setRecords(await fetchRsvpRecords());
    setLoadedAt(new Date());
  };

  const totalInvited  = GUEST_LIST.reduce((n, e) => n + 1 + (e.partnerFirst ? 1 : 0), 0);
  const acceptPct     = totalInvited > 0 ? Math.round((accepting.length / totalInvited) * 100) : 0;
  const declinePct    = totalInvited > 0 ? Math.round((declining.length / totalInvited) * 100) : 0;
  const pendingCount  = noResponse.length + pendingGuests.length;
  const pendingPct    = totalInvited > 0 ? Math.round((pendingCount / totalInvited) * 100) : 0;
  const avgParty      = records.length > 0 ? (allRows.length / records.length).toFixed(1) : '—';

  if (!authed) {
    return (
      <div className="password-gate">
        <div className="hero-photo-layer" style={{ backgroundImage: "url('/Sign in Page.jpeg')" }} />
        <form onSubmit={handleLogin} style={{ background: 'white', padding: '40px', borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)', width: '320px', textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <h2 style={{ fontFamily: 'var(--font-serif)', marginBottom: '24px', fontSize: '1.4rem' }}>Admin Console Login</h2>
          <div style={{ position: 'relative', marginBottom: '8px' }}>
            <input type={showPw ? 'text' : 'password'} placeholder="Admin password" value={pw}
              onChange={e => { setPw(e.target.value.toUpperCase()); setPwError(false); }}
              style={{ width: '100%', padding: '10px 40px 10px 14px', border: `1.5px solid ${pwError ? '#ef4444' : '#d1d5db'}`,
                borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }}
            />
            <button type="button" onClick={() => setShowPw(!showPw)}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#9ca3af' }}>
              {showPw ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/><line x1="4" y1="4" x2="20" y2="20"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>
          {pwError && <p style={{ color: '#ef4444', fontSize: '0.72rem', marginBottom: '8px' }}>Incorrect password</p>}
          <button type="submit" style={{ width: '100%', padding: '12px', background: 'var(--green, #2d6a4f)',
            color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }}>
            View Responses
          </button>
        </form>
      </div>
    );
  }

  const TABS = [
    { key: 'wedding',   label: 'Wedding Overview' },
    { key: 'rehearsal', label: 'Rehearsal Dinner' },
    { key: 'welcome',   label: 'Welcome Party' },
    { key: 'meals',     label: 'Meal Selection' },
    { key: 'rentals',   label: 'Rentals' },
    { key: 'contracts', label: 'Vendor Contracts' },
    { key: 'brand',     label: 'Brand Portal' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#faf9f6', padding: '40px 20px' }}>
      <div style={{ maxWidth: tab === 'brand' ? 'none' : tab === 'wedding' || tab === 'rentals' || tab === 'contracts' ? '1400px' : '1000px', margin: '0 auto', transition: 'max-width 0.3s ease' }}>

        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', marginBottom: '0' }}>RSVP Responses</h1>
        </div>

        {loading && <p style={{ color: '#6b7280', textAlign: 'center', padding: '60px' }}>Loading responses…</p>}
        {error   && <p style={{ color: '#ef4444', textAlign: 'center', padding: '60px' }}>{error}</p>}

        {!loading && !error && (
          <>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '28px', flexWrap: 'wrap' }}>
              {TABS.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{
                  padding: '8px 20px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600',
                  border: '1.5px solid', cursor: 'pointer',
                  background: tab === t.key ? '#1f2937' : 'white',
                  borderColor: tab === t.key ? '#1f2937' : '#d1d5db',
                  color: tab === t.key ? 'white' : '#374151',
                }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* ── WEDDING OVERVIEW (default dashboard) ────────────────────── */}
            {tab === 'wedding' && (
              <>
                {/* Hero stat cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '14px', marginBottom: '28px' }}>
                  <StatCard value={totalInvited}     label="Total Invited"     bg="#f0f9ff" color="#0369a1" />
                  <StatCard value={accepting.length} label="Accepting"         bg="#d1fae5" color="#065f46" />
                  <StatCard value={declining.length} label="Declined"          bg="#f3f4f6" color="#6b7280" />
                  <StatCard value={pendingCount}     label="Pending"           bg="#fef3c7" color="#92400e" />
                </div>

                {/* Donut chart + Key metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '28px' }}>
                  <div style={{ background: 'white', borderRadius: '14px', padding: '28px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DonutChart
                      segments={[
                        { value: acceptPct, color: '#10b981', label: 'Accepts', count: accepting.length },
                        { value: declinePct, color: '#d1d5db', label: 'Declines', count: declining.length },
                        { value: pendingPct, color: '#fbbf24', label: 'Pending', count: pendingCount },
                      ]}
                      centerLabel={`${acceptPct}%`}
                      centerSub="Acceptance Rate"
                    />
                  </div>
                  <div style={{ background: 'white', borderRadius: '14px', padding: '24px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    <h3 style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase',
                      letterSpacing: '0.08em', color: '#9ca3af', marginBottom: '16px', marginTop: 0 }}>Key Metrics</h3>
                    <MetricRow label="Acceptance Rate" value={`${acceptPct}%`} color="#065f46" />
                    <MetricRow label="Decline Rate" value={`${declinePct}%`} color="#6b7280" />
                    <MetricRow label="Pending Response" value={`${pendingPct}%`} color="#92400e" />
                    <MetricRow label="Total Invited" value={totalInvited} color="#0369a1" />
                  </div>
                </div>

                {/* Accept / Decline split bar */}
                <SplitStat
                  leftVal={accepting.length}  leftLabel="Accepts with Pleasure"  leftColor="#065f46"  leftBg="#d1fae5"
                  rightVal={declining.length} rightLabel="Declines with Regrets" rightColor="#6b7280" rightBg="#f3f4f6"
                  total={totalInvited}
                />

                {/* Refresh responses without reloading the page (which would sign you out) */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px',
                  marginBottom: '12px', fontSize: '0.72rem', color: '#9ca3af' }}>
                  {refreshError
                    ? <span style={{ color: '#ef4444' }}>Couldn't refresh — try again</span>
                    : loadedAt && <span>Last refreshed {loadedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>}
                  <button type="button" onClick={handleRefresh} disabled={refreshing} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 16px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '600',
                    border: '1.5px solid #d1d5db', background: 'white', color: '#374151',
                    cursor: refreshing ? 'default' : 'pointer', opacity: refreshing ? 0.6 : 1,
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                    {refreshing ? 'Refreshing…' : 'Refresh'}
                  </button>
                  <button type="button" onClick={() => setHistoryOpen(true)} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 16px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '600',
                    border: '1.5px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer',
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                    </svg>
                    History{history.length > 0 && <span style={{ opacity: 0.5, fontWeight: '500' }}>{history.length}</span>}
                  </button>
                  <button type="button" onClick={() => setExportOpen(true)} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 16px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '600',
                    border: '1.5px solid #1f2937', background: '#1f2937', color: 'white', cursor: 'pointer',
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Export
                  </button>
                </div>
                {exportOpen && <ExportModal rows={[...allRows, ...pendingGuests]} onClose={() => setExportOpen(false)} />}
                {historyOpen && (
                  <HistoryModal entries={history} onClose={() => setHistoryOpen(false)}
                    onRestore={h => saveResponse(h.party, h.name, h.before || { ...EMPTY_STATUS }, 'restore')} />
                )}

                {/* Guest table — every invited guest, latest responses first, then pending.
                    Editable here: the pencil on each row opens dropdowns for that guest. */}
                <GuestTable
                  rows={[...allRows, ...pendingGuests]}
                  columns={['Name','Party','Wedding','Rehearsal','Welcome Party','Meal','Dietary','Updated']}
                  onSave={(row, status) => saveResponse(row.party, row.name, status)}
                />
              </>
            )}

            {/* ── REHEARSAL DINNER (Wednesday, invited parties only) ────────── */}
            {tab === 'rehearsal' && (
              <>
                <div style={{ background: 'white', borderRadius: '14px', padding: '24px 28px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
                  <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '16px' }}>
                    Wednesday, October 28 at The Old Collier Golf Club — <strong>{REHEARSAL_KEYS.size}</strong> guests invited:
                  </p>
                  <SplitStat
                    leftVal={rehearsalYes.length} leftLabel="Attending Rehearsal Dinner" leftColor="#1d4ed8" leftBg="#dbeafe"
                    rightVal={rehearsalNo.length} rightLabel="Unable to Attend"           rightColor="#6b7280" rightBg="#f3f4f6"
                    total={REHEARSAL_KEYS.size}
                  />
                </div>
                <GuestTable
                  rows={[...rehearsalRows, ...rehearsalPending]}
                  columns={['Name','Party','Rehearsal','Updated']}
                />
              </>
            )}

            {/* ── WELCOME PARTY (Thursday) ──────────────────────────────────── */}
            {tab === 'welcome' && (
              <>
                <div style={{ background: 'white', borderRadius: '14px', padding: '24px 28px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
                  <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '16px' }}>
                    Thursday, October 29 at LaPlaya Beach Club — all <strong>{totalInvited}</strong> invited guests are welcome:
                  </p>
                  <SplitStat
                    leftVal={welcomeYes.length}  leftLabel="Attending Welcome Party"  leftColor="#92400e"  leftBg="#fef9c3"
                    rightVal={welcomeNo.length}  rightLabel="Unable to Attend"        rightColor="#6b7280" rightBg="#f3f4f6"
                    total={totalInvited}
                  />
                </div>
                <GuestTable
                  rows={[...allRows, ...pendingGuests]}
                  columns={['Name','Party','Welcome Party','Updated']}
                />
              </>
            )}

            {/* ── MEAL SELECTION ────────────────────────────────────────────── */}
            {tab === 'meals' && (
              <>
                <div style={{ background: 'white', borderRadius: '14px', padding: '28px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '28px' }}>
                    <StatCard value={meatRows.length} label="Beef Tenderloin"   bg="#fef2f2" color="#991b1b" sub={totalMeals > 0 ? `${Math.round(meatRows.length/totalMeals*100)}%` : '—'} />
                    <StatCard value={fishRows.length} label="Chilean Seabass"   bg="#eff6ff" color="#1d4ed8" sub={totalMeals > 0 ? `${Math.round(fishRows.length/totalMeals*100)}%` : '—'} />
                    <StatCard value={vegRows.length}  label="Vegetable Lasagna" bg="#f0fdf4" color="#166534" sub={totalMeals > 0 ? `${Math.round(vegRows.length/totalMeals*100)}%` : '—'} />
                  </div>
                  <DistBar label="Beef Tenderloin"   count={meatRows.length} total={totalMeals} bg="#991b1b" color="#991b1b" />
                  <DistBar label="Chilean Seabass"   count={fishRows.length} total={totalMeals} bg="#1d4ed8" color="#1d4ed8" />
                  <DistBar label="Vegetable Lasagna" count={vegRows.length}  total={totalMeals} bg="#166534" color="#166534" />
                  {dietaryRows.length > 0 && (
                    <p style={{ marginTop: '16px', fontSize: '0.75rem', color: '#c2410c', fontWeight: '500' }}>
                      {dietaryRows.length} guest{dietaryRows.length > 1 ? 's have' : ' has'} noted dietary restrictions — see Wedding Overview for details.
                    </p>
                  )}
                </div>
                <GuestTable
                  rows={accepting}
                  columns={['Name','Party','Meal','Dietary','Updated']}
                />
              </>
            )}

            {/* ── RENTALS ─────────────────────────────────────────────────── */}
            {tab === 'rentals' && (
              <Suspense fallback={<p style={{ color: '#6b7280', textAlign: 'center', padding: '60px' }}>Loading rentals hub…</p>}>
                <RentalsHub />
              </Suspense>
            )}

            {/* ── VENDOR CONTRACTS ────────────────────────────────────────── */}
            {tab === 'contracts' && (
              <Suspense fallback={<p style={{ color: '#6b7280', textAlign: 'center', padding: '60px' }}>Loading contracts…</p>}>
                <VendorContracts />
              </Suspense>
            )}

            {/* ── BRAND PORTAL ────────────────────────────────────────────── */}
            {tab === 'brand' && (
              <div style={{ margin: '0 -20px -40px' }}>
                <iframe
                  src="/brand-portal.html"
                  title="Wedding Brand Portal"
                  style={{ width: '100%', height: 'calc(100vh - 170px)', minHeight: '420px', border: 0, display: 'block', background: '#29402C' }}
                />
              </div>
            )}

            {tab !== 'brand' && (
              <p style={{ marginTop: '24px', fontSize: '0.72rem', color: '#9ca3af', textAlign: 'center' }}>
                Full data at <a href="https://console.firebase.google.com" target="_blank" rel="noreferrer"
                  style={{ color: '#6b7280' }}>console.firebase.google.com</a>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
